import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { isNotNullOrUndefined, System } from 'ngx-core-services';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, shareReplay, takeUntil, distinctUntilChanged, first } from 'rxjs/operators';
import { ColumnConfig, ColumnTypeEnum } from 'ngx-zcs-components';
import { CommunicationChannelType, DeviceUpgradeConfig } from 'ngx-modbus';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { selectedSystemInvertersWithLoggerData, selectedSystemMonitorPref, InverterWithLoggerData, SystemsState } from '../../store';
import { InverterTableRow } from './configurations-block.model';
import {
  InverterStatusFacade,
  calculateModuleStatus,
  DeviceActionStrategy,
  getDeviceActionStrategy,
} from '../shared/inverter-status';
import { AccessSupportService } from '../../communication/access-support.service';
import { createDebugLogger, DebugConfig } from '../shared/debug';
import { UPGRADE_COMMON_LABELS } from '../shared/labels';
import { ONE_CLICK_LABELS } from '../upgrade-inverter/one-click/one-click.labels';

const DEBUG_CONFIG: DebugConfig = {
  ENABLED: false,
  LOG_PERFORMANCE: false,
  LOG_API: false,
  LOG_STATE: false,
  LOG_DATA: false,
  PERFORMANCE_THRESHOLD_MS: 1000,
};

const Logger = createDebugLogger('[ConfigurationsBlock]', DEBUG_CONFIG);

const getLabels = () => ({
  ...UPGRADE_COMMON_LABELS,
  ...ONE_CLICK_LABELS,
  "Common.change": "Change",
  "Common.disable": "Disable",
  "Common.doNoUse": "Do not use",
  "Common.enable": "Enable",
  "Common.forceNow": "Force now",
  "Common.save": "Save",
  "Common.saveFailed": "Save failed",
  "Common.saving": "Saving...",
  "Common.trigger": "Trigger",
  "DeviceInfoPage.aRPC": "ARPC",
  "DeviceInfoPage.ct": "CT",
  "DeviceInfoPage.electricityMeter": "Electricity Meter",
  "DeviceSettingsPage.antiRefluxControl": "Zero Emission Control",
  "DeviceSettingsPage.antiRefluxSettings": "Zero Emission Settings",
  "DeviceSettingsPage.batteryActiveControl": "Battery Active Control",
  "DeviceSettingsPage.batteryActiveOneShot": "Battery Active One Shot",
  "DeviceSettingsPage.batteryActiveSettings": "Battery Active Settings",
  "DeviceSettingsPage.batteryCommunicationAddress": "Battery Communication Address - {{address}}",
  "DeviceSettingsPage.batteryCommunicationProtocol": "Battery Communication Protocol",
  "DeviceSettingsPage.batteryConfigID": "Battery Pack Config ID - {{ID}}",
  "DeviceSettingsPage.batteryConfiguration": "Battery Configuration",
  "DeviceSettingsPage.batteryCurrentChargeLimit": "Battery Current Charge Limit - {{limit}}A",
  "DeviceSettingsPage.batteryCurrentDischargeLimit": "Battery Current Discharge Limit - {{limit}}A",
  "DeviceSettingsPage.batteryDepthOfDischarge": "Battery Depth of Discharge - {{Discharge}}%",
  "DeviceSettingsPage.batteryEndOfDischarge": "EPS Battery Discharge Depth - {{Discharge}}%",
  "DeviceSettingsPage.batteryPowerLowerLimit": "Battery Power Lower Limit",
  "DeviceSettingsPage.batteryPowerUpperLimit": "Battery Power Upper Limit",
  "DeviceSettingsPage.chargingEndHourAndMinute": "Charging end hour and minute",
  "DeviceSettingsPage.chargingStartHourAndMinute": "Charging start hour and minute",
  "DeviceSettingsPage.desireGridPower": "Desire Grid Power",
  "DeviceSettingsPage.disabledDischargingAndCharging": "Disabled discharging and charging",
  "DeviceSettingsPage.disableEmergencyPowerSupply": "disabled",
  "DeviceSettingsPage.disableRefluxPower": "Disable reflux power",
  "DeviceSettingsPage.enabledDischargingAndCharging": "Enabled discharging and charging",
  "DeviceSettingsPage.enabledOnlyCharging": "Enabled only charging",
  "DeviceSettingsPage.enabledOnlyDischarging": "Enabled only discharging",
  "DeviceSettingsPage.enableEmergencyPowerSupplyEnableColdStart": "Enabled",
  "DeviceSettingsPage.energyStorageModeControl": "Work Mode",
  "DeviceSettingsPage.ePSControl": "EPS Control",
  "DeviceSettingsPage.ePSSettings": "EPS Settings",
  "DeviceSettingsPage.even": "Even",
  "DeviceSettingsPage.generatorMode": "Generator mode",
  "DeviceSettingsPage.highMark": "High/Mark",
  "DeviceSettingsPage.inputModeSettings": "Input Mode Settings",
  "DeviceSettingsPage.IVCurveScanControl": "IV Curve Scan Control",
  "DeviceSettingsPage.IVCurveScanPeriod": "IV Curve Scan Period - {{period}} min",
  "DeviceSettingsPage.IVCurveScanSettings": "IV Curve Scan Settings",
  "DeviceSettingsPage.lowSpace": "Even",
  "DeviceSettingsPage.modbusConfiguration": "Modbus Configuration",
  "DeviceSettingsPage.noCheckNone": "No Check/None (Default)",
  "DeviceSettingsPage.odd": "Odd",
  "DeviceSettingsPage.offGridMode": "Off-grid mode",
  "DeviceSettingsPage.parallelAddress": "Parallel Address",
  "DeviceSettingsPage.parallelControl": "Parallel Control",
  "DeviceSettingsPage.parallelMasterSlave": "Parallel Master/Slave",
  "DeviceSettingsPage.parallelSettings": "Parallel Settings",
  "DeviceSettingsPage.passiveMode": "Passive mode",
  "DeviceSettingsPage.PCCSampleMode": "PCC Sample Mode",
  "DeviceSettingsPage.peakClippingMode": "Peak clipping mode",
  "DeviceSettingsPage.powerRefluxEnableWithMeaningValue": "Lim.three-phase",
  "DeviceSettingsPage.powerRefluxEnableWithMinimumPower": "Zero Emission",
  "DeviceSettingsPage.refluxPower": "Reflux Power",
  "DeviceSettingsPage.rS485BaudRateSelection": "RS485 Baud Rate Selection - {{configBaud}}",
  "DeviceSettingsPage.rS485ConfigAddress": "RS485 Config Address - {{address}}",
  "DeviceSettingsPage.rS485ParityBitSelection": "RS485 Parity Bit Selection - {{configParityBit}}",
  "DeviceSettingsPage.rS485StopBitSelection": "RS485 Stop Bit Selection - {{configStopBit}}",
  "DeviceSettingsPage.spontaneousSelfUseMode": "Automatic mode",
  "DeviceSettingsPage.theMonthAndDateWhenTheRuleBecomesEffective": "The month and date when the rule becomes effective",
  "DeviceSettingsPage.theMonthAndDateWhenTheRuleCeasesToTakeEffect": "The month and date when the rule ceases to take effect",
  "DeviceSettingsPage.timeOfUseElectricityPriceModel": "Time-of-use electricity price model",
  "DeviceSettingsPage.timeOfUseElectricityPriceRuleNumber": "Time-of-use electricity price-rule number",
  "DeviceSettingsPage.timeOfUseElectricityPriceWeekWhenTheRuleTakesEffect": "Time-of-use electricity price-week when the rule takes effect",
  "DeviceSettingsPage.timeOfUsePriceMandatoryChargingCutOffSOC": "Time-of-use price-mandatory charging cut-off SOC",
  "DeviceSettingsPage.timeOfUsePriceMandatoryChargingPower": "Time-of-use price-mandatory charging power",
  "DeviceSettingsPage.timeOfUsePriceRuleEnable": "Time-of-use price-rule enable",
  "DeviceSettingsPage.timingChargeEnd": "Timing Charge End",
  "DeviceSettingsPage.timingChargePower": "Timing Charge Power",
  "DeviceSettingsPage.timingChargeStart": "Timing Charge Start",
  "DeviceSettingsPage.timingChargingAndDischargingMode": "Timing charging and discharging mode",
  "DeviceSettingsPage.timingDischargeEnd": "Timing Discharge End",
  "DeviceSettingsPage.timingDischargePower": "Timing Discharge Power",
  "DeviceSettingsPage.timingDischargeStart": "Timing Discharge Start",
  "DeviceSettingsPage.timingID": "Rule id",
  "DeviceSettingsPage.timingOffOnControl": "Timing Off On Control",
  "DeviceSettingsPage.unbalancedSupportControl": "Unbalanced Support Control",
  "DeviceSettingsPage.unbalancedSupportSettings": "Unbalanced Support Settings",
  "DeviceSettingsPage.workModeSettings": "Work Mode Settings",
  back: 'Back',
  configure: 'Configure',
  confirmBatchMessage: 'Do you want to update the following modules?\n\n{moduleList}',
  confirmMessage: 'Do you want to update the module {module} from {currentVersion} to {latestVersion}?',
  inverterConfigurationDescription: 'By accessing this it will be possible to set some parameters of your inverter',
  inverterConfigurationTitle: 'Configure your inverter',
  inverterNotUpgradableWarning: 'It is not possible to make remote commands to this device',
  inverterSelected: 'Inverter selected',
  inverterSelectionPlaceholder: 'Select inverter',
  localeCode: 'en-EN',
  noUpdates: 'No updates available.',
  proceed: 'Proceed',
  refresh: 'Refresh status',
  serialNumber: 'Serial number',
  stickLogger: 'Stick logger',
  title: 'Device details',
  updateDescription: "By accessing this section you can update the firmware of your inverter",
  updateError: 'Error during update',
  updateRequiredDescription: "Before you can access the configuration section it is necessary to update the firmware of one or more modules of your inverter",
  updateRequiredTitle: "Inverter upgrade required",
  updateSuccess: 'Update started successfully',
  updateTitle: "Update your inverter's firmware",
  deviceNotSupported: 'This device firmware is not supported for remote upgrade operations.',
  inconsistentFw: 'An inconsistency in the firmware versions has been detected. Please continue trying to update the required modules.',
  loadSafetyButton: 'Load safety configuration',
  rateLimited: 'Too many requests. Please try again later.',
  updatingMessage: 'Updating in progress, please wait...',
  upgrade: 'Update',
  upgradeTitleContent: 'In this section it will be possible to update the inverter modules, each of these may take a few minutes',
  safetyTitle: 'Upgrade safety',
  tableTitle: 'Plant Devices',
  status: 'Status',
  online: 'Online',
  offline: 'Offline',
  unknown: 'Unknown',
  logger: 'Logger',
  loggerSn: 'Logger SN',
  refreshAll: 'Refresh All',
  loading: 'Loading...',
  error: 'Error',
  noDevices: 'No devices found',
  upgrading: 'Upgrading...',
  notCertifiedTooltip: 'Device not in certified plant. Add it to enable upgrade and commands.',
  ahStatusDownloading: 'Downloading...',
  ahStatusDownloaded: 'Download complete',
  ahStatusDownloadFailed: 'Download interrupted',
  ahStatusUploading: 'Uploading {progress}%',
  ahStatusUploadError: 'Upload error',
  ahStatusVerified: 'Verification complete',
  ahStatusVerifyError: 'Verification error',
  ahStatusComplete: 'Complete',
  ahSendingUpgrade: 'Sending upgrade command...',
});

interface InverterUpgradeConfigLocal {
  ARM?: Array<{ LATEST_VERSION?: string; MIN_VERSION?: string }>;
  DSPM?: Array<{ LATEST_VERSION?: string; MIN_VERSION?: string }>;
  DSPS?: Array<{ LATEST_VERSION?: string; MIN_VERSION?: string }>;
}

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-768': 768,
})
@Component({
  selector: 'configurations-block',
  templateUrl: './configurations-block.component.html',
  styleUrls: ['./configurations-block.component.scss'],
})
export class ConfigurationsBlockComponent implements ICustomBlock, IReduxBlock, OnInit, OnDestroy {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();
  readonly _instanceId$: Observable<string> = new Observable<string>();
  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  // Template references for azzurro-table custom columns
  @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<any>;
  @ViewChild('loggerStatusTemplate', { static: true }) loggerStatusTemplate!: TemplateRef<any>;
  @ViewChild('armStatusTemplate', { static: true }) armStatusTemplate!: TemplateRef<any>;
  @ViewChild('dspmStatusTemplate', { static: true }) dspmStatusTemplate!: TemplateRef<any>;
  @ViewChild('dspsStatusTemplate', { static: true }) dspsStatusTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;

  // Table rows state
  readonly inverterRows$ = new BehaviorSubject<InverterTableRow[]>([]);
  readonly isLoadingTable$ = new BehaviorSubject<boolean>(true);

  // Data observable for azzurro-table
  // - null = loading state (shows skeleton)
  // - [] = no data (shows noResultTemplate)
  // - [...] = data (shows table rows)
  readonly tableData$: Observable<InverterTableRow[] | null> = combineLatest([
    this.inverterRows$,
    this.isLoadingTable$,
  ]).pipe(
    map(([rows, isLoading]) => {
      // When still loading and no rows yet, show loading skeleton
      if (isLoading && rows.length === 0) {
        return null;
      }
      // Otherwise return the rows (empty array will trigger noResultTemplate)
      return rows;
    }),
  );

  // Column configuration for azzurro-table (initialized in ngOnInit after templates are available)
  tableColumns: ColumnConfig[] = [];

  // Get inverters with logger data from store (pre-populated from aws.loggers)
  // Reacts to real-time changes from the GraphQL subscription
  readonly inverters$ = this.store.select(selectedSystemInvertersWithLoggerData).pipe(
    isNotNullOrUndefined(),
    // Emit when device list or any relevant data changes
    distinctUntilChanged((prev, curr) =>
      prev.length === curr.length &&
      prev.every((p, i) =>
        p.id === curr[i].id &&
        p.arm === curr[i].arm &&
        p.dspm === curr[i].dspm &&
        p.dsps === curr[i].dsps &&
        p.connectionStatus === curr[i].connectionStatus &&
        p.loggerFirmwareVer === curr[i].loggerFirmwareVer &&
        p.fotaStatus === curr[i].fotaStatus &&
        p.modbusAdr === curr[i].modbusAdr &&
        p.ahUpdating === curr[i].ahUpdating &&
        p.ahGatewayOnline === curr[i].ahGatewayOnline &&
        p.ahOperational === curr[i].ahOperational
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly selectedInverter$ = new BehaviorSubject<string | undefined>(undefined);
  readonly configurationsState$ = new BehaviorSubject<'table' | 'upgrade' | 'config'>('table');

  // Monitoring-type state
  isAzzurroHub = false;
  private actionStrategy: DeviceActionStrategy = getDeviceActionStrategy(false);
  gatewaySN?: string;
  channelType?: CommunicationChannelType;
  selectedModbusAddress?: number;

  /**
   * Tracks if the selected device firmware is not supported for remote upgrades.
   * Updated by lib-upgrade-inverter via deviceNotSupportedChange event.
   */
  isSelectedDeviceNotSupported: boolean = false;

  // S3 firmware config cache (loaded once per session)
  private firmwareConfigCache = new Map<string, DeviceUpgradeConfig>();
  private firmwareConfigLoaded = false;
  private isLoadingFirmwareConfig = false;

  // fota_status query cache (workaround for subscription issues)
  private fotaStatusCache = new Map<string, boolean>();
  private fotaStatusLoaded = false;
  private isLoadingFotaStatus = false;

  // Latest devices from store (kept in sync for use in updateTableRowsFromStore)
  private latestDevices: InverterWithLoggerData[] = [];

  private refreshSubscription?: Subscription;
  private upgradeStateSubscriptions = new Map<string, Subscription>();
  private destroy$ = new Subject<void>();

  constructor(
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
    private readonly inverterStatusFacade: InverterStatusFacade,
    private readonly accessSupportService: AccessSupportService,
  ) {}

  ngOnInit(): void {
    Logger.info('Component initialized (subscription-based, no polling)');

    // Detect AzzurroHub monitoring type
    this.store.select(selectedSystemMonitorPref).pipe(
      first(),
    ).subscribe(monitoringType => {
      this.isAzzurroHub = monitoringType === 'AzzurroHub';
      this.actionStrategy = getDeviceActionStrategy(this.isAzzurroHub);
      if (this.isAzzurroHub) {
        this.channelType = CommunicationChannelType.InternetAH_ZCS;
        Logger.info('AzzurroHub system detected — AH channel will be used');
      }
    });

    // Initialize table columns configuration
    this.tableColumns = [
      {
        columnDef: 'serialNumber',
        header: this.labels.serialNumber,
        type: ColumnTypeEnum.Text,
        cell: (row: InverterTableRow) => row.serialNumber,
      },
      {
        columnDef: 'loggerSn',
        header: this.labels.loggerSn,
        type: ColumnTypeEnum.Text,
        cell: (row: InverterTableRow) => row.loggerSn || '-',
      },
      {
        columnDef: 'status',
        header: this.labels.status,
        type: ColumnTypeEnum.Template,
        template: this.statusTemplate,
      },
      {
        columnDef: 'logger',
        header: this.labels.logger,
        type: ColumnTypeEnum.Template,
        template: this.loggerStatusTemplate,
      },
      {
        columnDef: 'arm',
        header: 'ARM',
        type: ColumnTypeEnum.Template,
        template: this.armStatusTemplate,
      },
      {
        columnDef: 'dspm',
        header: 'DSPM',
        type: ColumnTypeEnum.Template,
        template: this.dspmStatusTemplate,
      },
      {
        columnDef: 'dsps',
        header: 'DSPS',
        type: ColumnTypeEnum.Template,
        template: this.dspsStatusTemplate,
      },
      {
        columnDef: 'actions',
        header: this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.actionsTemplate,
      },
    ];

    // Subscribe to store changes - this replaces both initializeTableRows + polling
    this.inverters$.pipe(
      takeUntil(this.destroy$),
    ).subscribe((devices) => {
      Logger.state('Store data changed', {
        count: devices.length,
        devices: devices.map(d => ({
          id: d.id,
          arm: d.arm,
          dspm: d.dspm,
          dsps: d.dsps,
          connectionStatus: d.connectionStatus,
          fotaStatus: d.fotaStatus,
        })),
      });

      this.latestDevices = devices;

      // Set first inverter as selected if none selected
      if (devices.length > 0 && !this.selectedInverter$.value) {
        this.selectedInverter$.next(devices[0].id);
      }

      // Ensure upgrade state subscriptions are set up for all devices
      devices.forEach(device => this.subscribeToUpgradeState(device.id));

      // Sync fota_status from store to the upgrade BehaviorSubject
      this.syncFotaStatusFromStore(devices);

      // Update table rows with real-time data from store
      this.updateTableRowsFromStore(devices);

      // AH systems: fota_status comes from store (ahUpdating), skip Solarman queries.
      // S3 firmware config IS loaded to get latest versions and upgrade links.
      if (this.isAzzurroHub) {
        this.fotaStatusLoaded = true;
        if (devices.length > 0 && !this.firmwareConfigLoaded && !this.isLoadingFirmwareConfig) {
          this.loadFirmwareConfigs(devices);
        }
        return;
      }

      // On first data arrival, load firmware configs from S3 (once)
      if (devices.length > 0 && !this.firmwareConfigLoaded && !this.isLoadingFirmwareConfig) {
        this.loadFirmwareConfigs(devices);
      }

      // On first data arrival, query fota_status directly (workaround for subscription issues)
      if (devices.length > 0 && !this.fotaStatusLoaded && !this.isLoadingFotaStatus) {
        this.loadFotaStatuses(devices);
      }
    });

    // Manual refresh: invalidate caches and reload (skip for AH — data comes from store)
    this.refreshSubscription = this.refresh$.subscribe(() => {
      Logger.info('Manual refresh triggered');
      if (this.isAzzurroHub) {
        this.updateTableRowsFromStore(this.latestDevices);
        return;
      }
      this.firmwareConfigCache.clear();
      this.firmwareConfigLoaded = false;
      this.fotaStatusCache.clear();
      this.fotaStatusLoaded = false;
      if (this.latestDevices.length > 0) {
        this.loadFirmwareConfigs(this.latestDevices);
        this.loadFotaStatuses(this.latestDevices);
      }
    });
  }

  ngOnDestroy(): void {
    Logger.info('Component destroyed');
    this.refreshSubscription?.unsubscribe();
    // Clean up all upgrade state subscriptions
    this.upgradeStateSubscriptions.forEach((sub) => sub.unsubscribe());
    this.upgradeStateSubscriptions.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Updates table rows using real-time data from the store.
   * If S3 firmware config cache is available, calculates ModuleStatus (current vs latest).
   * Otherwise, keeps status as 'loading'.
   */
  private updateTableRowsFromStore(devices: InverterWithLoggerData[]): void {
    const currentRows = this.inverterRows$.value;

    const newRows: InverterTableRow[] = devices.map((device) => {
      const existingRow = currentRows.find(r => r.serialNumber === device.id);
      const isOnline = device.connectionStatus === 'connected' ? true
        : device.connectionStatus === 'disconnected' ? false
        : null;

      // For AH: use ahUpdating (per-device only — ahOperational=busy is plant-level and blocks actions, not status).
      // For Solarman: use fotaStatusCache or store.
      const isUpgrading = this.isAzzurroHub
        ? (device.ahUpdating ?? false)
        : (this.fotaStatusLoaded
          ? (this.fotaStatusCache.get(device.id) ?? device.fotaStatus === '1')
          : (device.fotaStatus === '1'));

      // Current firmware versions from store (strip leading "0" from AH versions)
      const cleanFw = (v?: string | null) => v?.replace(/^0+(?=[A-Za-z])/, '').toUpperCase();
      const armCurrent = cleanFw(device.arm);
      const dspmCurrent = cleanFw(device.dspm);
      const dspsCurrent = cleanFw(device.dsps);
      const loggerFirmwareCurrent = device.loggerFirmwareVer ?? undefined;

      // Latest versions from S3 cache (if available)
      const inverterConfig = this.firmwareConfigCache.get(device.id) as InverterUpgradeConfigLocal | undefined;
      const armLatest = inverterConfig?.ARM?.[0]?.LATEST_VERSION;
      const dspmLatest = inverterConfig?.DSPM?.[0]?.LATEST_VERSION;
      const dspsLatest = inverterConfig?.DSPS?.[0]?.LATEST_VERSION;

      let loggerLatestVersion: string | undefined;
      if (loggerFirmwareCurrent) {
        const loggerConfig = this.firmwareConfigCache.get(loggerFirmwareCurrent);
        loggerLatestVersion = (loggerConfig as { LATEST_SOLARMAN_LOGGER_BY_WEB?: string } | undefined)?.LATEST_SOLARMAN_LOGGER_BY_WEB;
      }

      // Calculate module statuses
      let armStatus = this.firmwareConfigLoaded
        ? calculateModuleStatus(armCurrent, armLatest)
        : 'loading' as const;
      let dspmStatus = this.firmwareConfigLoaded
        ? calculateModuleStatus(dspmCurrent, dspmLatest)
        : 'loading' as const;
      let dspsStatus = this.firmwareConfigLoaded
        ? calculateModuleStatus(dspsCurrent, dspsLatest)
        : 'loading' as const;
      let loggerStatus = this.firmwareConfigLoaded
        ? calculateModuleStatus(loggerFirmwareCurrent, loggerLatestVersion, { isLogger: true })
        : 'loading' as const;

      return {
        serialNumber: device.id,
        loggerSn: device.loggerId || '-',
        isOnline,
        // Preserve communicationChannel from existing row (only available on manual refresh)
        communicationChannel: existingRow?.communicationChannel ?? null,
        loggerStatus,
        loggerCurrentVersion: loggerFirmwareCurrent,
        loggerLatestVersion,
        armStatus,
        armCurrentVersion: armCurrent,
        armLatestVersion: armLatest,
        dspmStatus,
        dspmCurrentVersion: dspmCurrent,
        dspmLatestVersion: dspmLatest,
        dspsStatus,
        dspsCurrentVersion: dspsCurrent,
        dspsLatestVersion: dspsLatest,
        canConfigure: this.actionStrategy.canConfigure({
          isOnline, loggerStatus, isUpgrading,
          ahOperational: device.ahOperational, isCertified: device.isCertified,
        }),
        canUpgrade: this.actionStrategy.canUpgrade({
          isOnline, loggerStatus, isUpgrading,
          ahOperational: device.ahOperational, isCertified: device.isCertified,
        }),
        isUpgrading,
        isCertified: device.isCertified ?? true,
        isLoading: !this.firmwareConfigLoaded,
        lastUpdated: new Date(),
      };
    });

    this.inverterRows$.next(newRows);
  }

  /**
   * Syncs fota_status from store to the InverterStatusFacade upgrade BehaviorSubject.
   * This keeps the cross-component communication (upgrade-inverter <-> configurations-block) working.
   */
  private syncFotaStatusFromStore(devices: InverterWithLoggerData[]): void {
    for (const device of devices) {
      // Skip if the direct query already provided a more reliable value
      if (this.fotaStatusLoaded && this.fotaStatusCache.has(device.id)) continue;
      const isUpgradingFromStore = device.fotaStatus === '1';
      this.inverterStatusFacade.setUpgradeInProgress(device.id, isUpgradingFromStore);
    }
  }

  /**
   * Loads firmware config from S3 for all devices (once per session).
   * After loading, rebuilds table rows with correct ModuleStatus values.
   */
  private async loadFirmwareConfigs(devices: InverterWithLoggerData[]): Promise<void> {
    if (this.isLoadingFirmwareConfig) {
      return;
    }

    this.isLoadingFirmwareConfig = true;
    this.isLoadingTable$.next(true);
    Logger.info('Loading firmware configs from S3');

    try {
      // Collect unique serial numbers for inverter configs
      const inverterSerials = [...new Set(devices.map(d => d.id))];
      // Collect unique logger firmware versions for logger configs
      const loggerFirmwares = [...new Set(
        devices.map(d => d.loggerFirmwareVer).filter((fw): fw is string => !!fw)
      )];

      // Load all configs in parallel
      const inverterPromises = inverterSerials.map(async (sn) => {
        if (this.firmwareConfigCache.has(sn)) return;
        try {
          const config = await this.accessSupportService.getDeviceModuleInfo(sn);
          if (config) {
            this.firmwareConfigCache.set(sn, config);
          }
        } catch (e) {
          Logger.warn(`Failed to load firmware config for inverter ${sn}`, e);
        }
      });

      const loggerPromises = loggerFirmwares.map(async (fw) => {
        if (this.firmwareConfigCache.has(fw)) return;
        try {
          const config = await this.accessSupportService.getDeviceModuleInfo(fw);
          if (config) {
            this.firmwareConfigCache.set(fw, config);
          }
        } catch (e) {
          Logger.warn(`Failed to load firmware config for logger ${fw}`, e);
        }
      });

      await Promise.all([...inverterPromises, ...loggerPromises]);

      this.firmwareConfigLoaded = true;
      Logger.success('Firmware configs loaded', {
        inverterConfigs: inverterSerials.length,
        loggerConfigs: loggerFirmwares.length,
        cacheSize: this.firmwareConfigCache.size,
      });

      // Rebuild rows with correct ModuleStatus now that S3 data is available
      this.updateTableRowsFromStore(this.latestDevices);

    } catch (error) {
      Logger.error('Failed to load firmware configs', error);
    } finally {
      this.isLoadingTable$.next(false);
      this.isLoadingFirmwareConfig = false;
    }
  }

  /**
   * Loads fota_status via direct query for all devices (one-shot, no polling).
   * Workaround for subscription/cloud issues with fota_status.
   * The facade internally calls setUpgradeInProgress, and subscribeToUpgradeState updates rows.
   */
  private async loadFotaStatuses(devices: InverterWithLoggerData[]): Promise<void> {
    if (this.isLoadingFotaStatus) {
      return;
    }

    this.isLoadingFotaStatus = true;
    Logger.info('Loading fota_status via query (one-shot)');

    try {
      const promises = devices.map((device) => {
        return new Promise<void>((resolve) => {
          this.inverterStatusFacade.refreshInverterStatusMinimal(device.id, device.loggerId)
            .pipe(first())
            .subscribe({
              next: (status) => {
                this.fotaStatusCache.set(device.id, status.isUpgrading);
                Logger.api(`[${device.id}] fota_status from query: isUpgrading=${status.isUpgrading}`);
                resolve();
              },
              error: (err) => {
                Logger.warn(`[${device.id}] Failed to query fota_status`, err);
                resolve();
              },
            });
        });
      });

      await Promise.all(promises);

      this.fotaStatusLoaded = true;
      Logger.success('fota_status loaded for all devices', {
        cacheSize: this.fotaStatusCache.size,
      });

      // Rebuild rows with correct fota_status from query
      this.updateTableRowsFromStore(this.latestDevices);
    } catch (error) {
      Logger.error('Failed to load fota_status', error);
    } finally {
      this.isLoadingFotaStatus = false;
    }
  }

  /**
   * Subscribe to real-time upgrade state changes for a specific inverter.
   * This ensures the table shows upgrade status immediately when an upgrade
   * is started from the upgrade-inverter component.
   */
  private subscribeToUpgradeState(serialNumber: string): void {
    // Don't create duplicate subscriptions
    if (this.upgradeStateSubscriptions.has(serialNumber)) {
      return;
    }

    const subscription = this.inverterStatusFacade.isUpgradeInProgress(serialNumber).pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
    ).subscribe((isUpgrading) => {
      Logger.state(`[${serialNumber}] Upgrade state changed`, { isUpgrading });

      // Update the specific row's isUpgrading state
      const currentRows = this.inverterRows$.value;
      const rowIndex = currentRows.findIndex(r => r.serialNumber === serialNumber);

      if (rowIndex !== -1) {
        const updatedRows = [...currentRows];
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          isUpgrading,
          // Also update canConfigure based on upgrade state
          canConfigure: updatedRows[rowIndex].canConfigure && !isUpgrading,
        };
        this.inverterRows$.next(updatedRows);
      }
    });

    this.upgradeStateSubscriptions.set(serialNumber, subscription);
  }

  // Navigation methods
  goToUpgrade(serialNumber: string): void {
    Logger.state('Navigation: table -> upgrade', { serialNumber });
    // Reset device support status when navigating to a new device
    this.isSelectedDeviceNotSupported = false;

    // For AH systems, resolve gatewaySN from the store data
    if (this.isAzzurroHub) {
      const device = this.latestDevices.find(d => d.id === serialNumber);
      this.gatewaySN = device?.loggerId ?? undefined;
    }

    this.selectedInverter$.next(serialNumber);
    this.configurationsState$.next('upgrade');
  }

  goToConfig(serialNumber: string): void {
    Logger.state('Navigation: table -> config', { serialNumber });

    // For AH systems, resolve gatewaySN and modbusAddress from the store data
    if (this.isAzzurroHub) {
      const device = this.latestDevices.find(d => d.id === serialNumber);
      this.gatewaySN = device?.loggerId ?? undefined;
      this.selectedModbusAddress = device?.modbusAdr;
      Logger.state('AH config context', { gatewaySN: this.gatewaySN, modbusAddress: this.selectedModbusAddress });
    }

    this.selectedInverter$.next(serialNumber);
    this.configurationsState$.next('config');
  }

  goBackToTable(): void {
    const previousState = this.configurationsState$.value;
    Logger.state(`Navigation: ${previousState} -> table`);
    // Reset device support status when going back to table
    this.isSelectedDeviceNotSupported = false;
    this.configurationsState$.next('table');
  }

  manualRefresh(): void {
    Logger.info('Manual refresh requested');
    this.refresh$.next();
  }

  /**
   * Handles device support status change from lib-upgrade-inverter.
   * Hides the info section when device is not supported.
   */
  onDeviceNotSupportedChange(isNotSupported: boolean): void {
    Logger.state('Device support status changed', { isNotSupported });
    this.isSelectedDeviceNotSupported = isNotSupported;
  }
}
